// The widget's two stateless result views, and the two constants that go with
// them. Presentation only: no hooks, no data access, nothing to know about
// the panel's state machine. Split out of App.tsx.
import { type ArticleListItem } from "./data";
import { FolderIcon, NoResultIcon } from "./icons";

// A retrieval source the loaded list doesn't know (e.g. the list is stale)
// still gets a row; the source carries only slug and title.
export const EMPTY_ITEM: ArticleListItem = {
  id: "", slug: "", title: "", folder: "", tags: [], contextKeys: [], updatedAt: "",
};

export const RELEVANT_EMPTY = {
  title: "No relevant articles found",
  text: "We couldn't find any relevant articles for you. Use search or browse all articles.",
};

/**
 * Nothing matched. Sized and worded per the V2.0 design, which centres the
 * block in the body rather than hanging it from the top — hence `centre`,
 * used wherever the state owns the whole body.
 *
 * The Relevant tab has its own wording (RELEVANT_EMPTY), used both while
 * browsing it and for a search that came back empty with that tab open: on
 * that tab the reader is asking about *this page*, not about the whole KB.
 */
export function EmptyResults({
  title = "No results found",
  text = "We couldn't find any articles matching your search. Try different keywords, or browse the categories in the sidebar.",
  centre,
}: {
  title?: string;
  text?: string;
  centre?: boolean;
}) {
  return (
    <div class={`kb-empty${centre ? " centre" : ""}`} role="status">
      <span class="kb-empty-glyph"><NoResultIcon /></span>
      <p class="kb-empty-title">{title}</p>
      <p class="kb-empty-text">{text}</p>
    </div>
  );
}

export function ArticleRow({
  article,
  showFolder,
  onOpen,
}: {
  article: ArticleListItem;
  showFolder?: boolean;
  onOpen: (slug: string) => void;
}) {
  return (
    <button class="kb-row" onClick={() => onOpen(article.slug)}>
      <span class="kb-row-title">{article.title}</span>
      {showFolder && article.folder && (
        <span class="kb-row-folder">
          <FolderIcon class="kb-row-folder-icon" />
          {article.folder.split("/").join(" / ")}
        </span>
      )}
    </button>
  );
}
